import {
    RuleActionType,
    TransactionRule,
    TransactionRuleAction,
    TransactionRuleGroup,
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
} from "@common/types/transactionRule";
import { mapRuleActionTypeToForm } from "../../../settings/Rules/TransactionRuleForm/constants";
import {
    conditionFormRowToModel,
    formActionsToModel,
} from "../../../settings/Rules/TransactionRuleForm/formToModel.helpers";
import { canonicalizeConditions } from "./canonicalizeConditions";
import { ActionRowDefinition, ActionRowRuleMatch } from "./types";

export type ActionRowRuleMatchMap = Record<string, ActionRowRuleMatch | null>;

interface ClassifyActionRowRuleMatchesParams {
    rows: ActionRowDefinition[];
    selectedGroup?: TransactionRuleGroup;
    scopeConditions: ActionRowDefinition["conditions"];
}

type ModelAction = ReturnType<typeof formActionsToModel>[number];

export function classifyActionRowRuleMatches({
    rows,
    selectedGroup,
    scopeConditions,
}: ClassifyActionRowRuleMatchesParams): ActionRowRuleMatchMap {
    const out: ActionRowRuleMatchMap = {};
    const rules = selectedGroup?.rules ?? [];

    for (const row of rows) {
        out[row.id] = null;
        if (!row.createRule) {
            continue;
        }

        if (row.conditions.length === 0) {
            continue;
        }

        const rowConditions = [...scopeConditions, ...row.conditions]
            .map(conditionFormRowToModel)
            .filter((c): c is NonNullable<typeof c> => !!c);
        const rowModelAction = formActionsToModel([row.action])[0];
        if (rowConditions.length === 0 || !rowModelAction) {
            continue;
        }

        const canon = canonicalizeConditions(rowConditions);
        const matchingRules = rules.filter(
            (rule: TransactionRule) =>
                canonicalizeConditions(rule.conditions) === canon,
        );
        if (matchingRules.length === 0) {
            continue;
        }

        const match = classifyAgainstRules({
            matchingRules,
            row,
            rowModelAction,
        });
        out[row.id] = match;
    }

    return out;
}

function classifyAgainstRules({
    matchingRules,
    row,
    rowModelAction,
}: {
    matchingRules: TransactionRule[];
    row: ActionRowDefinition;
    rowModelAction: ModelAction;
}): ActionRowRuleMatch {
    const mergeCandidates: TransactionRule[] = [];
    const skipCandidates: Array<{
        rule: TransactionRule;
        action: TransactionRuleAction;
    }> = [];
    const conflictCandidates: Array<{
        rule: TransactionRule;
        action: TransactionRuleAction;
    }> = [];

    for (const rule of matchingRules) {
        const sameTypeExistingAction = rule.actions.find((action) =>
            isSameFormActionType(action, row),
        );

        if (!sameTypeExistingAction) {
            mergeCandidates.push(rule);
            continue;
        }

        if (
            areActionsEquivalent(
                sameTypeExistingAction,
                rowModelAction,
                rowModelAction.actionType,
            )
        ) {
            skipCandidates.push({
                rule,
                action: sameTypeExistingAction,
            });
            continue;
        }

        conflictCandidates.push({
            rule,
            action: sameTypeExistingAction,
        });
    }

    const conflict = conflictCandidates[0];
    if (conflict) {
        return buildSameTypeMatch({
            kind: "conflict_same_type_diff_value",
            rule: conflict.rule,
            sameTypeAction: conflict.action,
        });
    }

    const skip = skipCandidates[0];
    if (skip) {
        return buildSameTypeMatch({
            kind: "skip_same_value",
            rule: skip.rule,
            sameTypeAction: skip.action,
        });
    }

    const mergeRule = mergeCandidates[0] ?? matchingRules[0];
    return {
        kind: "merge",
        existingRuleId: mergeRule.id,
        existingActions: snapshotActions(mergeRule),
        existingConditions: mergeRule.conditions,
        existingRuleSource: mergeRule.source,
    };
}

function isSameFormActionType(
    action: TransactionRuleAction,
    row: ActionRowDefinition,
): boolean {
    try {
        return mapRuleActionTypeToForm(action.actionType) === row.actionType;
    } catch {
        return false;
    }
}

function buildSameTypeMatch({
    kind,
    rule,
    sameTypeAction,
}: {
    kind: "skip_same_value" | "conflict_same_type_diff_value";
    rule: TransactionRule;
    sameTypeAction: TransactionRuleAction;
}): ActionRowRuleMatch {
    return {
        kind,
        existingRuleId: rule.id,
        existingActions: snapshotActions(rule),
        existingConditions: rule.conditions,
        existingRuleSource: rule.source,
        existingSameTypeAction: {
            actionType: sameTypeAction.actionType,
            value: sameTypeAction.value,
            transferDirection: sameTypeAction.transferDirection,
        },
    };
}

function snapshotActions(rule: TransactionRule) {
    return rule.actions.map((action) => ({
        actionType: action.actionType,
        value: action.value,
        transferDirection: action.transferDirection,
    }));
}

/**
 * Compare two model-shape rule actions. `actionType` is assumed already
 * matched by the caller, so only `value` and `transferDirection` need to be
 * compared.
 */
function areActionsEquivalent(
    existing: Pick<TransactionRuleAction, "value" | "transferDirection">,
    candidate: Pick<TransactionRuleAction, "value" | "transferDirection">,
    actionType: RuleActionType,
): boolean {
    if (
        (existing.transferDirection ?? null) !==
        (candidate.transferDirection ?? null)
    ) {
        return false;
    }
    if (actionType === RuleActionType.ACCOUNT_ID) {
        return areAccountIdValuesEquivalent(existing.value, candidate.value);
    }
    if (actionType === RuleActionType.ACCRUAL_ACCOUNT_ID) {
        return areAccrualAccountValuesEquivalent(
            existing.value,
            candidate.value,
        );
    }
    return areValuesEquivalentAsText(existing.value, candidate.value);
}

/**
 * A candidate `ACCOUNT_ID` action is always built for one business, so its
 * value is a single uuid. An existing rule's entity condition can name several
 * businesses, storing one uuid per business as an array. The candidate
 * matches when its account id is already covered by the existing set.
 */
function areAccountIdValuesEquivalent(
    existingValue: TransactionRuleAction["value"],
    candidateValue: TransactionRuleAction["value"],
): boolean {
    const existingAccountIds = parseAccountIdActionValue(existingValue);
    const candidateAccountIds = parseAccountIdActionValue(candidateValue);
    if (!existingAccountIds || !candidateAccountIds) {
        return areValuesEquivalentAsText(existingValue, candidateValue);
    }
    return candidateAccountIds.every((accountId) =>
        existingAccountIds.includes(accountId),
    );
}

function areAccrualAccountValuesEquivalent(
    existingValue: TransactionRuleAction["value"],
    candidateValue: TransactionRuleAction["value"],
): boolean {
    const existing = parseAccrualAccountActionValue(existingValue);
    const candidate = parseAccrualAccountActionValue(candidateValue);
    const existingAccountIds = existing.accountIds;
    const candidateAccountIds = candidate.accountIds;
    if (!existingAccountIds || !candidateAccountIds) {
        return areValuesEquivalentAsText(existingValue, candidateValue);
    }
    return (
        existing.autoConfirm === candidate.autoConfirm &&
        candidateAccountIds.every((accountId) =>
            existingAccountIds.includes(accountId),
        )
    );
}

function areValuesEquivalentAsText(
    existingValue: TransactionRuleAction["value"],
    candidateValue: TransactionRuleAction["value"],
): boolean {
    return (
        JSON.stringify(normalizeValue(existingValue)) ===
        JSON.stringify(normalizeValue(candidateValue))
    );
}

function normalizeValue(
    value: TransactionRuleAction["value"] | null | undefined,
): TransactionRuleAction["value"] | null {
    if (value == null) {
        return null;
    }
    if (Array.isArray(value)) {
        return [...value].sort((a, b) => String(a).localeCompare(String(b)));
    }
    return value;
}
